import * as Common from '@piccolohealth/pbs-common';
import { ChakraV3, Spin, useRequiredParams } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { ContentLayout } from '../../components/layouts/ContentLayout';
import { useTemplateQuery } from '../../graphql/hooks/useTemplateQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { ParticipantDocumentTemplateEditor } from './documents/ParticipantDocumentTemplateEditor';
import { ParticipantNoteTemplateEditor } from './notes/ParticipantNoteTemplateEditor';

export const Template = () => {
  const { templateId } = useRequiredParams<{ templateId: string }>();
  const { organization } = useAppContext();

  const { data, isLoading, error } = useTemplateQuery({
    templateId,
    organizationId: organization.id,
  });

  const template = data?.organization?.template as Common.Template | null;

  if (isLoading) {
    return <Spin />;
  }

  if (error) {
    return <PiccoloError error={error} />;
  }

  if (!template) {
    return (
      <PiccoloError
        error={
          new Common.PiccoloError({
            type: 'TemplateNotFound',
            message: 'Template not found',
          })
        }
      />
    );
  }

  const content = P.run(() => {
    switch (template.type) {
      case Common.TemplateType.ParticipantDocument:
        return <ParticipantDocumentTemplateEditor template={template} />;
      case Common.TemplateType.ParticipantNote:
        return <ParticipantNoteTemplateEditor template={template} />;
      default:
        return null;
    }
  });

  return (
    <ContentLayout
      headerContent={
        <ChakraV3.Stack align='start' gap={0}>
          <ChakraV3.Heading size='3xl' fontWeight='bold'>
            {template?.name}
          </ChakraV3.Heading>
          <ChakraV3.HStack align='center'>
            <ChakraV3.Text fontSize='sm' color='secondary'>
              {Common.renderTemplateType(template.type)}
            </ChakraV3.Text>
          </ChakraV3.HStack>
        </ChakraV3.Stack>
      }
    >
      {content}
    </ContentLayout>
  );
};
